<template>
    <div>
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && attribute.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="attribute"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="attribute"
                            @on-delete-confirm="onDeleteConfirm"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { StatusCodes as HTTP } from 'http-status-codes';
import AddEditForm from '@/components/attributes/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Attribute'
    },

    validations: {
        attribute: {
            value: {
                required
            },
            attributeGroupId: {
                required
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const {
            params: { id = null },
            query: { attributeGroupId = null }
        } = this.$route;

        return {
            title: id ? 'Edit attribute' : 'Add attribute',
            id,
            editMode: !!id,
            attribute: {
                attributeGroupId
            },
            attributeGroupId,
            serverErrors: []
        };
    },

    computed: {
        sections() {
            const sections = {};

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        tableUrl() {
            if (this.attribute.id) {
                return `/attributes?attributeGroupId=${this.attribute.attributeGroupId}`;
            }

            if (this.attributeGroupId) {
                return `/attributes?attributeGroupId=${this.attributeGroupId}`;
            }

            return '/attributes';
        },

        items() {
            return [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Attributes',
                    to: this.tableUrl
                },
                {
                    text: this.editMode ? 'Edit' : 'Add',
                    active: true
                }
            ];
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.attribute = await this.getAttribute(this.id);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addAttribute: 'attributes/store',
            deleteAttribute: 'attributes/destroy',
            getAttribute: 'attributes/show',
            updateAttribute: 'attributes/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                if (!this.editMode) {
                    const added = await this.addAttribute(this.attribute);

                    this.$router.push(`/attributes/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateAttribute({
                        id: this.id,
                        data: this.attribute
                    });

                    this.attribute.updatedAt = updatedAt;

                    this.$toaster('Attribute updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteAttribute(this.id);

                this.$router.push(this.tableUrl);
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        }
    }
};
</script>
